import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Copy, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "./ui/dialog";
import { Field, SalesIdChip } from "./common";
import { api, errMsg } from "../lib/api";

const EMPTY = { name: "", email: "", phone: "", city: "" };

export const AddExecutiveDialog = ({ onAdded }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const close = (o) => { setOpen(o); if (!o) { setF(EMPTY); setDone(null); } };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/executives", f);
      setDone({ ...data, link: window.location.origin + data.setup_path });
      onAdded();
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };
  const waText = done && encodeURIComponent(`Assalam-o-Alaikum ${done.name}, your CommissionWorld Sales Executive account is ready. Sales ID: ${done.sales_id}. Set your password here (valid 3 days): ${done.link}`);
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild><Button data-testid="add-exec-button"><UserPlus className="mr-2 h-4 w-4" />Add Sales Executive</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{done ? "Executive added" : "Add Sales Executive"}</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-4 text-sm" data-testid="add-exec-success">
            <div className="flex items-center justify-between"><span>{done.name}</span><SalesIdChip id={done.sales_id} /></div>
            <p className={done.emailed ? "text-emerald-700" : "text-amber-700"} data-testid="add-exec-email-status">
              {done.emailed ? `A "set your password" email was sent to ${done.email}.` : "The email could not be sent. Share the link below with the executive."}
            </p>
            <div className="break-all rounded-lg border bg-slate-50 p-2 font-mono text-xs" data-testid="add-exec-setup-link">{done.link}</div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { navigator.clipboard?.writeText(done.link); toast.success("Link copied"); }} data-testid="add-exec-copy"><Copy className="mr-2 h-4 w-4" />Copy link</Button>
              <Button asChild variant="outline" className="flex-1"><a href={`https://wa.me/${done.phone || ""}?text=${waText}`} target="_blank" rel="noreferrer" data-testid="add-exec-whatsapp"><MessageCircle className="mr-2 h-4 w-4" />WhatsApp</a></Button>
            </div>
            <p className="text-xs text-slate-500">The link is private and valid for 3 days. You can generate a new one later from Manage.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" id="add-exec-form">
            <Field label="Full name" required><Input data-testid="add-exec-name" required value={f.name} onChange={set("name")} className="h-11" /></Field>
            <Field label="Email" required hint="They will log in with this email"><Input data-testid="add-exec-email" type="email" required value={f.email} onChange={set("email")} className="h-11" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" required><Input data-testid="add-exec-phone" required placeholder="03XX-XXXXXXX" value={f.phone} onChange={set("phone")} className="h-11" /></Field>
              <Field label="City"><Input data-testid="add-exec-city" value={f.city} onChange={set("city")} className="h-11" /></Field>
            </div>
            <p className="text-xs text-slate-500">A unique Sales ID is assigned automatically. The executive chooses their own password — you never see it.</p>
          </form>
        )}
        <DialogFooter>
          {done ? <Button onClick={() => close(false)} data-testid="add-exec-done">Done</Button>
            : <Button type="submit" form="add-exec-form" disabled={busy} data-testid="add-exec-submit">{busy ? "Adding…" : "Add executive"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
